import { useEffect, useState } from "react";
import { GrapeshotResponse } from "@typings/Cue";
import axios from "axios";

type UseGrapeshotDataProps = {
  /** Relative url path of the article */
  urlPath: string;
  /** Index of the article in the list */
  index: number;
  /** Optional keywords to override Grapeshot data */
  keywords?: string;
};

const fetchGrapeshotData = async (urlPath: string): Promise<string> => {
  try {
    const gsChannelsCombined = await axios
      .get<GrapeshotResponse>("/_plat/api/v1/grapeshot", {
        params: { urlPath },
      })
      .then((item) => item.data?.channels.map((i) => i.name).join(","));

    return gsChannelsCombined || "no_analysis";
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `fetchGrapeshotData error for urlPath: ${urlPath}. Details: ${(error as Error).message}`
    );

    return "no_analysis";
  }
};

/**
 * Custom hook to fetch Grapeshot data based on the article's URL path and index.
 * If keywords are provided, it uses them directly instead of fetching from Grapeshot.
 */
export const useGrapeshotData = ({
  urlPath,
  index,
  keywords,
}: UseGrapeshotDataProps) => {
  const [gsChannels, setGsChannels] = useState<string>(
    keywords || "no_analysis"
  );

  useEffect(() => {
    if (keywords) return;
    if (index === 0) return;

    const callFetch = async () => {
      setGsChannels(await fetchGrapeshotData(urlPath));
    };

    callFetch();
  }, [urlPath, index, keywords]);

  return { gsChannels };
};
